import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { playKitchenBell } from '../utils/kitchenBell'
import { displayTable } from '../utils/tableLabel'
import { useAuth } from '../context/AuthContext'
import { useCatalog } from '../context/CatalogContext'
import {
  dismissKitchenTicket,
  finishKitchenTicket,
  reopenKitchenTicket,
  setKitchenItem,
  subscribeKitchen,
  subscribeOwnStations,
} from '../services/kitchen'
import { snakePath, snakeSlot } from '../utils/snake'
import { canPromptInstall, isIOS, isStandalone, onInstallChange, promptInstall } from '../utils/install'
import { BellIcon, BellOffIcon, CheckIcon, ChefHatIcon, SendToBackIcon, UndoIcon, UserIcon, XIcon } from '../components/icons'
import { ablative } from '../utils/turkish'
import { type KitchenItem, type KitchenTicket } from '../types'
import { stationNames } from '../utils/stations'
import './KitchenPage.css'

/**
 * Mutfak ekranı (kullanıcı, 11 Eki 2026).
 *
 * Tezgah fişinin ekrandaki karşılığı: seçilen tezgahın fişleri kıvrımlı bir
 * sırada. En eski fiş sıranın başında (1), yeni fiş alt köşeden girip yol
 * boyunca sabit hızda sıranın sonuna kayar. Ürüne dokunmak durumunu ilerletir
 * (Yeni -> Hazırlanıyor -> Hazır); "Hepsi hazır" fişi sıradan çıkarır,
 * arkadakiler yol boyunca bir adım ilerler.
 */

const CARD_H = 330
const GAP = 18
const MIN_CARD_W = 270
/** Yol üzerinde bir yerden ötekine geçiş süresi: kartın "belli bir hızı". */
const STEP_MS = 150
/** Bekleme uyarıları (Sipariş takip ile aynı): 10 dk sarı, 20 dk kırmızı. */
const WARN_MIN = 10
const LATE_MIN = 20

const stationKey = (tenant: string) => `adissoo.mutfak.tezgah.${tenant}`
const SOUND_KEY = 'adissoo.mutfak.ses'
/** Kart büyüklüğü yüzdesi, cihaz başına (tabletin ekranına göre). */
const SCALE_KEY = 'adissoo.mutfak.olcek'
const SCALE_MIN = 60
const SCALE_MAX = 150

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
function writeLocal(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* tercih bu cihazda kalmaz, sorun değil */
  }
}

const reduceMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

const minutesSince = (t: number, now: number) => Math.max(0, Math.floor((now - t) / 60_000))
const ago = (t: number, now: number) => {
  const m = minutesSince(t, now)
  return m === 0 ? 'şimdi' : `${m} dk`
}

const NEXT: Record<KitchenItem['status'], KitchenItem['status']> = {
  new: 'cooking',
  cooking: 'ready',
  ready: 'new',
}

export function KitchenPage() {
  const { tenantId, admin, isKitchen, kitchenBell } = useAuth()
  const { categories, products } = useCatalog()

  const [tickets, setTickets] = useState<KitchenTicket[]>([])
  const [loaded, setLoaded] = useState(false)
  /**
   * Ekranın (cihaz) tezgahları: Ayarlar > Mutfak ekranları'nda patron seçiyor,
   * tablette değiştirilemiyor (kullanıcı, 11 Eki 2026). Boş: bütün tezgahlar.
   * Patron ve kasa için kısıt yok.
   */
  const loginStations = useMemo<string[]>(() => {
    if (!isKitchen) return []
    const p = admin?.perms
    return p?.stations?.length ? p.stations : p?.station ? [p.station] : []
  }, [isKitchen, admin])
  // Girişte gelen tezgahlar, patron değiştirince canlı olarak yenisi.
  const [liveStations, setLiveStations] = useState<string[] | null>(null)
  useEffect(() => {
    if (!isKitchen || !admin?.uid) return
    return subscribeOwnStations(admin.uid, (next) =>
      setLiveStations((prev) => (prev && prev.join('|') === next.join('|') ? prev : next)),
    )
  }, [isKitchen, admin?.uid])
  const allowed = isKitchen && liveStations ? liveStations : loginStations
  const locked = allowed.length > 0
  const [station, setStationState] = useState<string>(() => {
    if (locked) return allowed.length === 1 ? allowed[0] : ''
    return (tenantId && readLocal(stationKey(tenantId))) ?? ''
  })
  // Tezgahlar değişince seçili tezgah artık izinli değilse: tek tezgahsa o,
  // değilse hepsi.
  const allowedKey = allowed.join('|')
  const firstAllowed = useRef(true)
  useEffect(() => {
    if (firstAllowed.current) {
      firstAllowed.current = false
      return
    }
    if (!locked) {
      setStationState('')
      return
    }
    setStationState((cur) => (allowed.length === 1 ? allowed[0] : cur && allowed.includes(cur) ? cur : ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedKey])
  const [sound, setSound] = useState(() => readLocal(SOUND_KEY) !== '0')
  const [scalePct, setScalePct] = useState(() => {
    const v = Number(readLocal(SCALE_KEY))
    return v >= SCALE_MIN && v <= SCALE_MAX ? v : 100
  })
  const changeScale = (v: number) => {
    const next = Math.min(SCALE_MAX, Math.max(SCALE_MIN, Math.round(v / 5) * 5))
    setScalePct(next)
    writeLocal(SCALE_KEY, String(next))
  }
  const [showDone, setShowDone] = useState(false)
  const [now, setNow] = useState(Date.now())
  /** "Hepsi hazır"a basılıp çıkış animasyonundaki fişler. */
  const [leaving, setLeaving] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  const setStation = (s: string) => {
    setStationState(s)
    if (tenantId) writeLocal(stationKey(tenantId), s)
  }

  useEffect(() => {
    if (!tenantId) return
    return subscribeKitchen(tenantId, (list) => {
      setTickets(list)
      setLoaded(true)
    })
  }, [tenantId])

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 15_000)
    return () => window.clearInterval(t)
  }, [])

  // Ekran açık kalsın: tablet uykuya geçip yeni fişleri kaçırmasın. Sekme
  // arka plana gidince kilit düşüyor, geri gelince yeniden isteniyor.
  useEffect(() => {
    type Lock = { release: () => Promise<void> }
    const wl = (navigator as unknown as { wakeLock?: { request: (t: 'screen') => Promise<Lock> } }).wakeLock
    if (!wl) return
    let lock: Lock | null = null
    const take = () => {
      if (document.visibilityState !== 'visible') return
      wl.request('screen').then((l) => (lock = l)).catch(() => {})
    }
    take()
    document.addEventListener('visibilitychange', take)
    return () => {
      document.removeEventListener('visibilitychange', take)
      void lock?.release().catch(() => {})
    }
  }, [])

  const stations = useMemo(() => {
    if (locked) return [...allowed].sort((a, b) => a.localeCompare(b, 'tr'))
    const set = new Set<string>(stationNames(categories, products))
    for (const t of tickets) set.add(t.station)
    return [...set].sort((a, b) => a.localeCompare(b, 'tr'))
  }, [categories, products, tickets, locked, allowed])

  const live = (t: KitchenTicket) => t.status === 'open' || (t.status === 'void' && !t.doneAt)
  const inScope = (t: KitchenTicket) => !locked || allowed.includes(t.station)
  const inStation = (t: KitchenTicket) => inScope(t) && (!station || t.station === station)

  const queue = useMemo(
    () => tickets.filter((t) => live(t) && inStation(t) && !leaving.has(t.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tickets, station, leaving],
  )
  const recentDone = useMemo(
    () =>
      tickets
        .filter((t) => t.status === 'done' && inStation(t) && t.doneAt && now - t.doneAt < 30 * 60_000)
        .sort((a, b) => (b.doneAt ?? 0) - (a.doneAt ?? 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tickets, station, now],
  )
  const openCount = (s: string) =>
    tickets.filter((t) => t.status === 'open' && inScope(t) && (!s || t.station === s)).length

  // Yeni fiş sesi: ilk yüklemede değil, sonradan gelenlerde.
  const seen = useRef<Set<string> | null>(null)
  useEffect(() => {
    if (!loaded) return
    const ids = new Set(queue.map((t) => t.id))
    if (seen.current && sound && queue.some((t) => t.status === 'open' && !seen.current!.has(t.id))) playKitchenBell(kitchenBell)
    seen.current = new Set([...(seen.current ?? []), ...ids])
  }, [queue, loaded, sound, kitchenBell])

  // ---- Ekranda anında, sonra sunucuda -----------------------------------
  const patch = (id: string, fn: (t: KitchenTicket) => KitchenTicket) =>
    setTickets((all) => all.map((t) => (t.id === id ? fn(t) : t)))

  const onItem = async (t: KitchenTicket, i: number) => {
    if (t.status !== 'open') return
    const next = NEXT[t.items[i].status]
    patch(t.id, (x) => ({
      ...x,
      items: x.items.map((it, k) =>
        k === i
          ? { ...it, status: next, startedAt: next === 'cooking' ? (it.startedAt ?? Date.now()) : next === 'new' ? undefined : it.startedAt }
          : it,
      ),
    }))
    try {
      await setKitchenItem(t.id, i, next)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const onFinish = async (t: KitchenTicket) => {
    setLeaving((s) => new Set(s).add(t.id))
    try {
      await finishKitchenTicket(t.id)
      patch(t.id, (x) => ({ ...x, status: 'done', doneAt: Date.now(), items: x.items.map((it) => ({ ...it, status: 'ready' })) }))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLeaving((s) => {
        const n = new Set(s)
        n.delete(t.id)
        return n
      })
    }
  }

  const onDismiss = async (t: KitchenTicket) => {
    patch(t.id, (x) => ({ ...x, doneAt: Date.now() }))
    try {
      await dismissKitchenTicket(t.id)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const onReopen = async (t: KitchenTicket) => {
    patch(t.id, (x) => ({ ...x, status: 'open', doneAt: undefined }))
    try {
      await reopenKitchenTicket(t.id)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const waitingItems = queue
    .filter((t) => t.status === 'open')
    .reduce((n, t) => n + t.items.filter((it) => it.status !== 'ready').reduce((m, it) => m + it.qty, 0), 0)

  return (
    <div className="kds">
      <header className="kds__bar">
        {locked && allowed.length === 1 ? (
          // Tek tezgaha bağlı ekran: şerit yok, yalnız tezgahın adı.
          <div className="kds__fixed">
            <b>{allowed[0]}</b>
            {openCount('') > 0 && <span>{openCount('')} fiş</span>}
          </div>
        ) : (
        <div className="kds__stations" role="tablist" aria-label="Tezgah">
          <button
            type="button"
            role="tab"
            aria-selected={!station}
            className={!station ? 'is-on' : ''}
            onClick={() => setStation('')}
          >
            {locked ? 'Hepsi' : 'Bütün tezgahlar'}
            {openCount('') > 0 && <b>{openCount('')}</b>}
          </button>
          {stations.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={station === s}
              className={station === s ? 'is-on' : ''}
              onClick={() => setStation(s)}
            >
              {s}
              {openCount(s) > 0 && <b>{openCount(s)}</b>}
            </button>
          ))}
        </div>
        )}
        <div className="kds__tools">
          <span className="kds__count">
            {queue.length} fiş, {waitingItems} ürün bekliyor
          </span>
          <button
            type="button"
            className={`kds__tool ${showDone ? 'is-on' : ''}`}
            onClick={() => setShowDone((v) => !v)}
            aria-pressed={showDone}
          >
            <UndoIcon />
            Son gönderilenler
          </button>
          <button
            type="button"
            className="kds__tool kds__tool--icon"
            onClick={() => {
              const next = !sound
              setSound(next)
              writeLocal(SOUND_KEY, next ? '1' : '0')
              if (next) playKitchenBell(kitchenBell)
            }}
            aria-pressed={sound}
            aria-label={sound ? 'Yeni fiş sesini kapat' : 'Yeni fiş sesini aç'}
            title={sound ? 'Ses açık' : 'Ses kapalı'}
          >
            {sound ? <BellIcon /> : <BellOffIcon />}
          </button>
        </div>
      </header>

      {isKitchen && <InstallBar />}

      {error && (
        <p className="kds__error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Kapat">
            <XIcon />
          </button>
        </p>
      )}

      <div className="kds__main">
        <SnakeQueue
          tickets={queue}
          now={now}
          showStation={!station}
          animate={loaded}
          onItem={onItem}
          onFinish={onFinish}
          onDismiss={onDismiss}
          empty={loaded ? (station ? `${station} için bekleyen fiş yok` : 'Bekleyen fiş yok') : 'Yükleniyor…'}
          emptyHint={loaded ? 'Yeni sipariş gelince burada sırayla dizilir' : undefined}
          scale={scalePct / 100}
        />

        {/* Kart büyüklüğü: sağ altta yüzen çubuk (kullanıcı, 11 Eki 2026). */}
        <div className={`kds__scale ${showDone ? 'is-shifted' : ''}`} role="group" aria-label="Kart büyüklüğü">
          <button type="button" onClick={() => changeScale(scalePct - 10)} aria-label="Kartları küçült">
            −
          </button>
          <input
            type="range"
            min={SCALE_MIN}
            max={SCALE_MAX}
            step={5}
            value={scalePct}
            onChange={(e) => changeScale(Number(e.target.value))}
            aria-label="Kart büyüklüğü yüzdesi"
          />
          <button type="button" onClick={() => changeScale(scalePct + 10)} aria-label="Kartları büyüt">
            +
          </button>
          <button
            type="button"
            className="kds__scale-pct"
            onClick={() => changeScale(100)}
            title="%100'e döndür"
          >
            %{scalePct}
          </button>
        </div>

        {showDone && (
          <aside className="kds__done" aria-label="Son gönderilenler">
            <div className="kds__done-head">
              <b>Son gönderilenler</b>
              <button type="button" className="kds__x" onClick={() => setShowDone(false)} aria-label="Kapat">
                <XIcon />
              </button>
            </div>
            {recentDone.length === 0 ? (
              <p className="kds__done-empty">Son yarım saatte gönderilen fiş yok.</p>
            ) : (
              <ul>
                {recentDone.map((t) => (
                  <li key={t.id}>
                    <div>
                      <b>{displayTable(t.label)}</b>
                      <span>
                        {t.items.map((it) => `${it.qty}× ${it.name}`).join(', ')}
                      </span>
                      <small>
                        {!station && `${t.station}, `}
                        {ago(t.doneAt!, now)} önce gönderildi
                      </small>
                    </div>
                    <button type="button" className="btn btn--outline" onClick={() => void onReopen(t)}>
                      <UndoIcon />
                      Geri al
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Kıvrımlı sıra
// ---------------------------------------------------------------------------

interface QueueProps {
  tickets: KitchenTicket[]
  now: number
  showStation: boolean
  /** İlk yüklemede kartlar yerinde belirir; sonradan gelenler yol boyunca kayar. */
  animate: boolean
  onItem: (t: KitchenTicket, i: number) => void
  onFinish: (t: KitchenTicket) => void
  onDismiss: (t: KitchenTicket) => void
  empty: string
  /** Boş ekranda başlığın altındaki kısa açıklama. */
  emptyHint?: string
  /** Kart ölçeği, 1 = %100. */
  scale: number
}

function SnakeQueue({
  tickets,
  now,
  showStation,
  animate,
  onItem,
  onFinish,
  onDismiss,
  empty,
  emptyHint,
  scale,
}: QueueProps) {
  const box = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    // İç ölçü: kenar boşluğu düşülüyor, yoksa sağ sütun ekrandan taşıyordu.
    const measure = () => {
      const cs = getComputedStyle(el)
      const px = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight)
      const py = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
      setSize({ w: el.clientWidth - px, h: el.clientHeight - py })
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    measure()
    return () => ro.disconnect()
  }, [])

  // Kart büyüklüğü (sağ alttaki yüzde): yerleşim küçültülmüş/büyütülmüş
  // genişlikte hesaplanıyor, şerit de aynı oranda "zoom"lanıyor.
  const W = size.w / scale
  const H = size.h / scale
  const n = tickets.length
  const cols = Math.max(1, Math.floor((W + GAP) / (MIN_CARD_W + GAP)))
  const cardW = cols > 0 ? (W - (cols - 1) * GAP) / cols : MIN_CARD_W
  const visRows = Math.max(1, Math.floor((H + GAP) / (CARD_H + GAP)))
  const rows = Math.max(visRows, Math.ceil(n / cols))
  const slots = rows * cols

  const pos = useCallback(
    (i: number) => {
      const s = snakeSlot(i, cols, rows)
      return { x: s.col * (cardW + GAP), y: s.row * (CARD_H + GAP) }
    },
    [cols, rows, cardW],
  )

  // Yol boyunca kayma: her kartın önceki yeri hatırlanıyor; yer değiştiyse
  // aradaki bütün yerlerden geçerek, satır dönüşlerinde kavis çizerek gidiyor.
  const els = useRef(new Map<string, HTMLElement>())
  const prevIdx = useRef(new Map<string, number>())
  const geo = useRef('')

  useLayoutEffect(() => {
    const g = `${cols}x${rows}x${Math.round(cardW)}`
    const geoChanged = geo.current !== '' && geo.current !== g
    geo.current = g
    const seenNow = new Set<string>()

    tickets.forEach((t, i) => {
      seenNow.add(t.id)
      const el = els.current.get(t.id)
      const before = prevIdx.current.get(t.id)
      prevIdx.current.set(t.id, i)
      if (!el || !animate || geoChanged || W === 0 || reduceMotion()) return
      if (before === i) return

      // Yeni fiş: alt köşedeki girişten sıranın sonuna. Eski fiş: önceki
      // yerinden yenisine (önündeki fiş çıktıysa bir adım ileri).
      const from = before ?? slots - 1
      const path = snakePath(from, i)
      const frames: Keyframe[] = []
      if (before === undefined) {
        const p = pos(slots - 1)
        // Giriş son satırın yönüne göre sağda ya da solda; kart dışarıdan gelir.
        const out = snakeSlot(slots - 1, cols, rows).col === cols - 1 ? 1 : -1
        frames.push({ transform: `translate(${p.x + out * cardW * 0.6}px, ${p.y}px)`, opacity: 0 })
      }
      path.forEach((k, j) => {
        const p = pos(k)
        if (j > 0) {
          const a = snakeSlot(path[j - 1], cols, rows)
          const b = snakeSlot(k, cols, rows)
          // Satır dönüşü: kenardan dışarı doğru küçük bir kavis.
          if (a.row !== b.row) {
            const side = a.col === cols - 1 ? 1 : -1
            const q = pos(path[j - 1])
            frames.push({ transform: `translate(${q.x + side * Math.min(60, cardW * 0.18)}px, ${(q.y + p.y) / 2}px)`, opacity: 1 })
          }
        }
        frames.push({ transform: `translate(${p.x}px, ${p.y}px)`, opacity: 1 })
      })
      if (frames.length < 2) return
      const steps = Math.max(1, path.length - 1 + (before === undefined ? 1 : 0))
      el.animate(frames, { duration: Math.min(steps, 30) * STEP_MS, easing: 'linear' })
    })

    for (const id of [...prevIdx.current.keys()]) if (!seenNow.has(id)) prevIdx.current.delete(id)
  }, [tickets, cols, rows, cardW, slots, pos, animate, W])

  // Arkadaki kıvrımlı yol çizgisi: sıranın nereden aktığını gösteriyor.
  const track = useMemo(() => {
    if (W === 0) return ''
    const used = Math.max(slots, 1)
    // Dönüşün yana taşma payı: yarım daire ekranın kenarına kadar
    // taşıyordu (kullanıcı, 11 Eki 2026). Kübik eğrinin en geniş yeri kontrol
    // noktasının 3/4'ü; kartın yarı genişliğini geçmesin.
    const bulge = Math.min(cardW / 2 - 14, 90)
    const ctrl = Math.max(0, bulge) / 0.75
    let d = ''
    for (let i = 0; i < used; i++) {
      const p = pos(i)
      const cx = p.x + cardW / 2
      const cy = p.y + CARD_H / 2
      if (i === 0) {
        d += `M ${cx} ${cy}`
        continue
      }
      const a = snakeSlot(i - 1, cols, rows)
      const b = snakeSlot(i, cols, rows)
      if (a.row !== b.row) {
        const side = a.col === cols - 1 ? 1 : -1
        const prevY = pos(i - 1).y + CARD_H / 2
        d += ` C ${cx + side * ctrl} ${prevY} ${cx + side * ctrl} ${cy} ${cx} ${cy}`
      } else d += ` L ${cx} ${cy}`
    }
    return d
  }, [W, slots, pos, cardW, cols, rows])

  const height = rows * (CARD_H + GAP) - GAP

  return (
    <div className="kds__queue" ref={box}>
      {/* Boşken noktalı yol gizli (1 Kas 2026, kullanıcı): ortada tek başına
          duran noktalar ile ondan kopuk yazı bir arıza gibi okunuyordu. */}
      {n === 0 && (
        <div className="kds__empty">
          <div className="kds__empty-card">
            <span className="kds__empty-icon" aria-hidden="true">
              <ChefHatIcon />
            </span>
            <p className="kds__empty-title">{empty}</p>
            {emptyHint && <p className="kds__empty-hint">{emptyHint}</p>}
          </div>
        </div>
      )}
      <div className="kds__lane" style={{ height, zoom: scale, visibility: n === 0 ? 'hidden' : undefined }}>
        <svg className="kds__track" width={W} height={height} aria-hidden="true">
          <path d={track} />
          <TrackArrows d={track} />
        </svg>
        {tickets.map((t, i) => {
          const p = pos(i)
          return (
            <TicketCard
              key={t.id}
              ref={(el) => {
                if (el) els.current.set(t.id, el)
                else els.current.delete(t.id)
              }}
              ticket={t}
              index={i}
              now={now}
              showStation={showStation}
              style={{ width: cardW, height: CARD_H, transform: `translate(${p.x}px, ${p.y}px)` }}
              onItem={onItem}
              onFinish={onFinish}
              onDismiss={onDismiss}
            />
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Fiş kartı
// ---------------------------------------------------------------------------

interface CardProps {
  ticket: KitchenTicket
  index: number
  now: number
  showStation: boolean
  style: CSSProperties
  onItem: (t: KitchenTicket, i: number) => void
  onFinish: (t: KitchenTicket) => void
  onDismiss: (t: KitchenTicket) => void
}

const ITEM_LABEL: Record<KitchenItem['status'], string> = {
  new: 'Yeni',
  cooking: 'Hazırlanıyor',
  ready: 'Hazır',
}


/** Oklar arası (px) ve hız (px/sn); önizlemede seçildi (1 Kas 2026, kullanıcı). */
const ARROW_GAP = 34
const ARROW_SPEED = 22

/**
 * Yol boyunca sıra yönünde (1 numaralı fişe doğru) akan küçük oklar.
 *
 * Her ok SVG'nin kendi animateMotion'ıyla yürüyor, JavaScript döngüsü yok:
 * mutfak tabletleri çoğu zaman zayıf cihazlar. Oklar eşit aralıkla, aynı
 * yolda farklı başlangıç anlarıyla diziliyor; uçlarda belirip kayboluyor.
 * Yol yalnız fişler yer değiştirince değişiyor, oklar o zaman yeniden diziliyor.
 */
function TrackArrows({ d }: { d: string }) {
  const probe = useRef<SVGPathElement>(null)
  const [len, setLen] = useState(0)
  useLayoutEffect(() => {
    setLen(d && probe.current ? probe.current.getTotalLength() : 0)
  }, [d])

  const n = len > ARROW_GAP ? Math.floor(len / ARROW_GAP) : 0
  const dur = len / ARROW_SPEED
  // Uçlarda 30 px'lik belirme/kaybolma payı, yolun oranı olarak.
  const f = len > 0 ? Math.min(0.2, 30 / len) : 0

  return (
    <>
      <path ref={probe} d={d} className="kds__track-probe" />
      {Array.from({ length: n }, (_, i) => {
        // Negatif başlangıç: oklar ilk anda yolun tamamına yayılmış geliyor.
        const begin = `${(-(i * ARROW_GAP) / ARROW_SPEED).toFixed(2)}s`
        return (
          <path key={`${d.length}-${i}`} className="kds__arrow" d="M -3 -5 L 3 0 L -3 5">
            {/* keyPoints 1;0: yolun sonundan başına, yani 1 numaralı fişe doğru.
                auto-reverse: ok da o yöne baksın. */}
            <animateMotion
              dur={`${dur.toFixed(2)}s`}
              begin={begin}
              repeatCount="indefinite"
              rotate="auto-reverse"
              keyPoints="1;0"
              keyTimes="0;1"
              calcMode="linear"
              path={d}
            />
            <animate
              attributeName="opacity"
              dur={`${dur.toFixed(2)}s`}
              begin={begin}
              repeatCount="indefinite"
              values="0;1;1;0"
              keyTimes={`0;${f.toFixed(3)};${(1 - f).toFixed(3)};1`}
            />
          </path>
        )
      })}
    </>
  )
}

const TicketCard = forwardRef<HTMLElement, CardProps>(function TicketCard(
  { ticket: t, index, now, showStation, style, onItem, onFinish, onDismiss },
  ref,
) {
  const mins = minutesSince(t.createdAt, now)
  const age = t.status === 'void' ? 'is-void' : mins >= LATE_MIN ? 'is-late' : mins >= WARN_MIN ? 'is-warn' : ''
  const allReady = t.items.every((it) => it.status === 'ready')

  return (
    <article className={`kt ${age}`} ref={ref} style={style} aria-label={`${index + 1}. sıra, ${displayTable(t.label)}`}>
      {/* Masa ve garson ayrı bir başlık bandında, ürünlerden keskin
          ayrılıyor (kullanıcı, 11 Eki 2026). */}
      <div className="kt__top">
        {/* Sıra numarası iki satır boyu: masa adı + garson. */}
        <span className="kt__no">{index + 1}</span>
        <b className="kt__label">{displayTable(t.label)}</b>
        <span className="kt__time">{ago(t.createdAt, now)}</span>
        <div className="kt__meta">
          {t.byName && (
            <span className="kt__by">
              <UserIcon />
              <span className="kt__by-name">{t.byName}</span>
            </span>
          )}
          {showStation && <span className="kt__station">{t.station}</span>}
        </div>
      </div>

      {/* Masa taşıma/birleştirme (23 Eki 2026): usta eski adı görmüş olabilir. */}
      {t.movedFrom && t.status !== 'void' && (
        <div className="kt__moved">
          <SendToBackIcon />
          <span>{ablative(t.movedFrom)} taşındı</span>
        </div>
      )}

      {t.status === 'void' ? (
        <div className="kt__void">
          <strong>İPTAL EDİLDİ</strong>
          <span>{t.items.map((it) => `${it.qty}× ${it.name}`).join(', ')}</span>
          <button type="button" className="btn btn--outline" onClick={() => onDismiss(t)}>
            Gördüm, kaldır
          </button>
        </div>
      ) : (
        <>
          <ul className="kt__items">
            {t.items.map((it, i) => (
              <li key={i}>
                <button type="button" className={`kt__item is-${it.status}`} onClick={() => onItem(t, i)}>
                  <span className="kt__qty">{it.qty}×</span>
                  <span className="kt__name">
                    {it.name}
                    {it.note && <em>{it.note}</em>}
                  </span>
                  <span className="kt__st">
                    {it.status === 'ready' && <CheckIcon />}
                    {ITEM_LABEL[it.status]}
                    {it.status === 'cooking' && it.startedAt && <small> {ago(it.startedAt, now)}</small>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className={`kt__done ${allReady ? 'is-ready' : ''}`} onClick={() => onFinish(t)}>
            <CheckIcon />
            Hepsi hazır
          </button>
        </>
      )}
    </article>
  )
})

// ---------------------------------------------------------------------------
// Ana ekrana ekle
// ---------------------------------------------------------------------------

const INSTALL_KEY = 'adissoo.mutfak.kurulum-kapatildi'

/**
 * Bağlanan tablette "ana ekrana ekle" çağrısı. Android'de düğme kurulum
 * penceresini açıyor; iPad'de Apple buna izin vermediği için yönerge.
 * Uygulama zaten ana ekrandan açıldıysa ya da kapatıldıysa görünmüyor.
 */
function InstallBar() {
  const [, force] = useState(0)
  const [closed, setClosed] = useState(() => readLocal(INSTALL_KEY) === '1')
  useEffect(() => onInstallChange(() => force((n) => n + 1)), [])

  if (closed || isStandalone()) return null
  const android = canPromptInstall()
  const ios = isIOS()
  if (!android && !ios) return null

  const close = () => {
    setClosed(true)
    writeLocal(INSTALL_KEY, '1')
  }

  return (
    <div className="kds__install" role="note">
      {android ? (
        <>
          <span>Mutfak ekranını ana ekrana ekleyin: tam ekran açılır, tek dokunuşla gelir.</span>
          <button type="button" className="btn btn--primary" onClick={() => void promptInstall()}>
            Ana ekrana ekle
          </button>
        </>
      ) : (
        <span>
          iPad'de ana ekrana eklemek için: Paylaş simgesi, sonra <b>Ana Ekrana Ekle</b>. Ardından ana
          ekrandaki Adissoo'yu açıp patrondan alacağınız <b>yeni kodla</b> bağlayın (ana ekrandaki
          uygulama Safari'deki bağlantıyı görmez).
        </span>
      )}
      <button type="button" className="kds__x" onClick={close} aria-label="Kapat">
        <XIcon />
      </button>
    </div>
  )
}
